import { useIngestAlerts } from "../hooks/useIngestAlerts";
import { useUiStore } from "../state/uiStore";
import type { IngestAlert } from "../api/types";
import {
  KIT_UPDATE_COMMANDS,
  formatRefusedDay,
  isKitOutdatedAlert,
  kitAlertState,
  kitCatchUpCommand,
} from "../lib/kitAlert";
import { plural } from "../lib/format";
import { Icons } from "./Icons";

function CommandBlock({ command, label }: { command: string; label: string }) {
  const pushToast = useUiStore((s) => s.pushToast);

  // Hors contexte sécurisé, navigator.clipboard est absent : l'appel lève et
  // la commande reste sélectionnable à la main.
  async function copy() {
    try {
      await navigator.clipboard.writeText(command);
      pushToast({ variant: "info", title: "Commande copiée" });
    } catch {
      pushToast({
        variant: "error",
        title: "Copie impossible",
        subtitle: "Sélectionnez la commande pour la copier.",
      });
    }
  }

  return (
    <div className="kit-alert-cmd">
      <pre>
        <code>{command}</code>
      </pre>
      <button
        type="button"
        className="btn sm"
        onClick={() => void copy()}
        aria-label={label}
      >
        Copier
      </button>
    </div>
  );
}

function KitOutdatedAlert({ alert }: { alert: IngestAlert }) {
  const state = kitAlertState(alert);
  const days = alert.refused_days ?? [];
  if (state === "resend" && days.length === 0) return null;

  const n = days.length;
  const title =
    state === "outdated"
      ? "Votre kit timesheet n'est plus à jour : vos heures ne sont pas enregistrées"
      : `Kit timesheet à jour, ${n} journée${plural(n)} ${n > 1 ? "restent" : "reste"} à renvoyer`;
  const installed =
    alert.client_version || `antérieure à ${alert.required_version}`;

  return (
    <section
      className={`kit-alert ${state}`}
      role={state === "outdated" ? "alert" : "status"}
    >
      <div className="kit-alert-title">
        <Icons.warn size={16} sw={2} />
        <span>{title}</span>
      </div>
      {state === "outdated" && (
        <>
          <p>
            Version installée : <strong>{installed}</strong> · version requise :{" "}
            <strong>{alert.required_version}</strong>
          </p>
          <p className="kit-alert-step">
            1. Mettez à jour le kit depuis un terminal :
          </p>
          <CommandBlock
            command={KIT_UPDATE_COMMANDS}
            label="Copier les commandes de mise à jour"
          />
        </>
      )}
      {n > 0 && (
        <>
          <p className="kit-alert-step">
            {state === "outdated" && "2. "}Renvoyez les journées refusées :
          </p>
          <ul className="kit-alert-days">
            {days.map((d) => (
              <li key={d}>{formatRefusedDay(d)}</li>
            ))}
          </ul>
          <CommandBlock
            command={kitCatchUpCommand(days)}
            label="Copier la commande de renvoi"
          />
          <p className="kit-alert-note">
            Ces journées ont été refusées par TAWA et doivent être renvoyées une
            fois le kit à jour ; ce bandeau disparaît quand c'est fait.
          </p>
        </>
      )}
    </section>
  );
}

export function IngestAlertBanner() {
  const { data } = useIngestAlerts();
  const alerts = (data?.alerts ?? []).filter(isKitOutdatedAlert);
  if (alerts.length === 0) return null;

  return (
    <>
      {alerts.map((a) => (
        <KitOutdatedAlert key={a.key_id} alert={a} />
      ))}
    </>
  );
}
